import React, { useState } from 'react';
import { GripVertical, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutPicker } from './LayoutPicker';
import { CardItem } from './CardItem';
import { UrlFetcher } from './UrlFetcher';
interface EditorCard {
  id: string;
  title: string;
  url: string;
  description: string;
  image: string;
  enabled: boolean;
  price?: string;
  status?: string;
}
interface EditorSection {
  id: string;
  type: 'listings-videos' | 'custom-links';
  title: string;
  description: string;
  layout: string;
  cards: EditorCard[];
  collapsed: boolean;
}
interface SectionCardProps {
  section: EditorSection;
  onUpdate: (id: string, updates: Partial<EditorSection>) => void;
  onDelete: (id: string) => void;
  onCardUpdate: (sectionId: string, cardId: string, updates: Partial<EditorCard>) => void;
  onCardDelete: (sectionId: string, cardId: string) => void;
  onCardAdd: (sectionId: string, card: Omit<EditorCard, 'id' | 'enabled'>) => void;
  onCardReorder: (sectionId: string, fromIndex: number, toIndex: number) => void;
  onDragStart: (e: React.DragEvent, sectionId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetSectionId: string) => void;
  isDragging: boolean;
}
export function SectionCard({
  section,
  onUpdate,
  onDelete,
  onCardUpdate,
  onCardDelete,
  onCardAdd,
  onCardReorder,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging
}: SectionCardProps) {
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const handleCardDragStart = (e: React.DragEvent, cardId: string) => {
    setDraggedCardId(cardId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleCardDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleCardDrop = (e: React.DragEvent, targetCardId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedCardId || draggedCardId === targetCardId) {
      setDraggedCardId(null);
      return;
    }
    const fromIndex = section.cards.findIndex(c => c.id === draggedCardId);
    const toIndex = section.cards.findIndex(c => c.id === targetCardId);
    if (fromIndex !== -1 && toIndex !== -1) {
      onCardReorder(section.id, fromIndex, toIndex);
    }
    setDraggedCardId(null);
  };
  const handleAddManualCard = () => {
    onCardAdd(section.id, {
      title: 'New Link',
      url: 'https://example.com',
      description: 'Add a description',
      image: ''
    });
    setShowManualAdd(false);
  };
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    onDelete(section.id);
    setShowDeleteConfirm(false);
  };
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };
  return <div draggable onDragStart={e => onDragStart(e, section.id)} onDragOver={onDragOver} onDrop={e => onDrop(e, section.id)} className={`bg-white rounded-2xl border border-brand-light shadow-sm transition-all ${isDragging ? 'opacity-50' : ''}`} data-id="element-228">
      <div className="p-6" data-id="element-229">
        <div className="flex items-start gap-3 mb-4" data-id="element-230">
          <button type="button" className="mt-2 text-brand-light hover:text-brand-muted cursor-grab active:cursor-grabbing transition-colors" data-id="element-231">
            <GripVertical size={20} data-id="element-232" />
          </button>

          <div className="flex-1 space-y-2" data-id="element-233">
            <div className="flex items-center gap-2" data-id="element-234">
              <input type="text" value={section.title} onChange={e => onUpdate(section.id, {
              title: e.target.value
            })} className="flex-1 text-lg font-medium text-brand-dark outline-none border-b-2 border-transparent hover:border-brand-light focus:border-brand-forest transition-colors" placeholder="Section title" data-id="element-235" />
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-brand-sage text-brand-forest border border-brand-sage" data-id="element-236">
                {section.type === 'listings-videos' ? 'Listings & Videos' : 'Custom Links'}
              </span>
            </div>
            <input type="text" value={section.description} onChange={e => onUpdate(section.id, {
            description: e.target.value
          })} className="w-full text-sm text-brand-muted outline-none border-b border-transparent hover:border-brand-light focus:border-brand-forest transition-colors" placeholder="Section description" data-id="element-237" />
          </div>

          <div className="flex items-center gap-1 shrink-0" data-id="element-238">
            <button type="button" onClick={() => onUpdate(section.id, {
            collapsed: !section.collapsed
          })} className="p-2 text-brand-muted hover:text-brand-dark hover:bg-brand-offwhite rounded-lg transition-colors" data-id="element-239">
              {section.collapsed ? <ChevronDown size={18} data-id="element-240" /> : <ChevronUp size={18} data-id="element-241" />}
            </button>
            <div className="relative" data-id="element-242">
              <button type="button" onClick={handleDeleteClick} className="p-2 text-brand-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-id="element-243">
                <Trash2 size={18} data-id="element-244" />
              </button>

              {showDeleteConfirm && <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-brand-light shadow-premium p-3 z-20" data-id="element-245">
                  <p className="text-xs font-medium text-brand-dark mb-3" data-id="element-246">
                    Delete this section?
                  </p>
                  <div className="flex gap-2" data-id="element-247">
                    <button onClick={cancelDelete} className="flex-1 px-2 py-1.5 text-xs font-medium text-brand-muted hover:bg-brand-offwhite rounded-lg transition-colors" data-id="element-248">
                      Cancel
                    </button>
                    <button onClick={confirmDelete} className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors" data-id="element-249">
                      Delete
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        <AnimatePresence data-id="element-250">
          {!section.collapsed && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-251">
              <div className="border-t border-brand-light pt-4 space-y-4" data-id="element-252">
                <LayoutPicker sectionType={section.type} selectedLayout={section.layout} onLayoutChange={layout => onUpdate(section.id, {
              layout
            })} data-id="element-253" />

                <div className="space-y-3" data-id="element-254">
                  {section.cards.map(card => <CardItem key={card.id} card={card} sectionType={section.type} onUpdate={(cardId, updates) => onCardUpdate(section.id, cardId, updates)} onDelete={cardId => onCardDelete(section.id, cardId)} onDragStart={handleCardDragStart} onDragOver={handleCardDragOver} onDrop={handleCardDrop} isDragging={draggedCardId === card.id} data-id="element-255" />)}
                </div>

                <div className="space-y-3 pt-2" data-id="element-256">
                  <UrlFetcher sectionType={section.type} onCardAdd={cardData => onCardAdd(section.id, cardData)} data-id="element-257" />
                  <button type="button" onClick={handleAddManualCard} className="text-sm text-brand-muted hover:text-brand-dark transition-colors" data-id="element-258">
                    or add manually
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}